import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface OnboardingProgress {
  completedTours: string[];
  skippedTours: string[];
  lastSeenAt: Date;
  preferences: {
    showToursOnFirstVisit: boolean;
    autoStartTours: boolean;
  };
}

export const useOnboarding = (moduleName: string) => {
  const [showTour, setShowTour] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    const checkFirstVisit = async () => {
      try {
        const progressRef = doc(db, 'onboardingProgress', user.uid);
        const progressDoc = await getDoc(progressRef);

        if (!progressDoc.exists()) {
          // First time user - show tour
          setShowTour(true);
          await setDoc(progressRef, {
            completedTours: [],
            skippedTours: [],
            lastSeenAt: new Date(),
            preferences: {
              showToursOnFirstVisit: true,
              autoStartTours: true
            }
          });
        } else {
          const data = progressDoc.data() as OnboardingProgress;
          const hasCompleted = data.completedTours.includes(moduleName);
          const hasSkipped = data.skippedTours.includes(moduleName);
          
          // Show tour if not completed and not skipped, and auto-start is enabled
          if (!hasCompleted && !hasSkipped && data.preferences.autoStartTours) {
            setShowTour(true);
          }
        }
      } catch (error) {
        console.error('Error checking onboarding progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkFirstVisit();
  }, [moduleName, user]);

  const completeTour = async () => {
    if (!user) return;

    try {
      const progressRef = doc(db, 'onboardingProgress', user.uid);
      const progressDoc = await getDoc(progressRef);

      if (progressDoc.exists()) {
        const data = progressDoc.data() as OnboardingProgress;
        await updateDoc(progressRef, {
          completedTours: [...data.completedTours, moduleName],
          lastSeenAt: new Date()
        });
      }
      
      setShowTour(false);
    } catch (error) {
      console.error('Error completing tour:', error);
    }
  };

  const skipTour = async () => {
    if (!user) return;

    try {
      const progressRef = doc(db, 'onboardingProgress', user.uid);
      const progressDoc = await getDoc(progressRef);

      if (progressDoc.exists()) {
        const data = progressDoc.data() as OnboardingProgress;
        await updateDoc(progressRef, {
          skippedTours: [...data.skippedTours, moduleName],
          lastSeenAt: new Date()
        });
      }
      
      setShowTour(false);
    } catch (error) {
      console.error('Error skipping tour:', error);
    }
  };

  const restartTour = () => {
    setShowTour(true);
  };

  return {
    showTour,
    setShowTour,
    completeTour,
    skipTour,
    restartTour,
    isLoading
  };
};
